import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'
import { SingleSelectCombobox, SingleSelectComboboxField } from '@/components/form-components/SingleSelectCombobox'
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Textarea } from '@/components/shadcn/textarea'
import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { useCurrentWorkspace } from '../useCurrentWorkspace'

const settingTypeOptions = [
  { value: 'KeyVault', label: 'KeyVault' },
  { value: 'string', label: 'string' },
  { value: 'number', label: 'number' },
  { value: 'boolean', label: 'boolean' },
  { value: 'date', label: 'date' },
  { value: 'list', label: 'list' },
] as const

type SettingType = (typeof settingTypeOptions)[number]['value']

const formSchema = z
  .object({
    key: z.string().min(1, 'Key is required').max(256, 'Key cannot exceed 256 characters'),
    value: z.string().optional().default(''),
    valueType: z.enum(['KeyVault', 'string', 'number', 'boolean', 'date', 'list']),
  })
  .superRefine((data, ctx) => {
    const value = data.value?.trim() ?? ''

    if (!value) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['value'],
        message: 'Value is required',
      })
      return
    }

    if (data.valueType === 'number' && !/^-?\d+$/.test(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['value'],
        message: 'Value must be an integer',
      })
    }

    if (data.valueType === 'boolean' && !['true', 'false'].includes(value.toLowerCase())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['value'],
        message: 'Value must be true or false',
      })
    }

    if (data.valueType === 'date') {
      const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value)
      const [year, month, day] = value.split('-').map(Number)
      const candidate = new Date(Date.UTC(year, (month || 1) - 1, day || 1))
      const isValidDate =
        candidate.getUTCFullYear() === year && candidate.getUTCMonth() + 1 === month && candidate.getUTCDate() === day
      if (!isDateOnly || !isValidDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['value'],
          message: 'Value must be a valid date in YYYY-MM-DD format',
        })
      }
    }

    if (data.valueType === 'list') {
      const listEntries = value
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean)
      if (!listEntries.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['value'],
          message: 'Enter at least one list value',
        })
      }
    }
  })

type TWorkspaceSettingForm = z.infer<typeof formSchema>
type ExistingWorkspaceSetting = RestSchemas['Messages.Workspaces.WorkspaceSetting']
type NewWorkspaceSetting = RestSchemas['Messages.Workspaces.NewWorkspaceSetting']
type ApplicationSetting = RestSchemas['Messages.Applications.ApplicationSetting']

type WorkspaceSettingFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedSetting?: ExistingWorkspaceSetting
}

export const WorkspaceSettingForm = (props: WorkspaceSettingFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)
  const { workspaceId, app_id } = useCurrentWorkspace()
  const edit = !!props.selectedSetting
  const {
    getWorkspacesApiGetWorkspaceSettings,
    putWorkspacesApiCreateWorkspaceSetting,
    postWorkspacesApiUpdateWorkspaceSetting,
    getApplicationsApiGetApplicationSettings,
  } = useRestClient()

  const applicationId = Number(app_id)
  const { data: applicationSettings = [] } = getApplicationsApiGetApplicationSettings.useQuery({
    query: { applicationId },
  })

  const invalidateSettings = useCallback(() => {
    getWorkspacesApiGetWorkspaceSettings.invalidateQueries({
      parameters: {
        query: { workspaceId },
      },
    })
  }, [getWorkspacesApiGetWorkspaceSettings, workspaceId])

  const { mutate: createSetting, isPending: isCreating } = putWorkspacesApiCreateWorkspaceSetting.useMutation(undefined, {
    onSuccess: () => {
      invalidateSettings()
      if (props.sheetTrigger) setSheetOpen(false)
      toast.success('Workspace setting created')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Failed to create workspace setting')
    },
  })

  const { mutate: updateSetting, isPending: isUpdating } = postWorkspacesApiUpdateWorkspaceSetting.useMutation(undefined, {
    onSuccess: () => {
      invalidateSettings()
      if (props.sheetTrigger) setSheetOpen(false)
      toast.success('Workspace setting updated')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Failed to update workspace setting')
    },
  })

  const submitHandler = useCallback(
    (data: TWorkspaceSettingForm) => {
      const normalizedValue =
        data.valueType === 'list'
          ? data.value
              .split(',')
              .map((entry) => entry.trim())
              .filter(Boolean)
              .join(',')
          : data.value

      const body: NewWorkspaceSetting = {
        workspaceId,
        key: props.selectedSetting?.key ?? data.key,
        value: normalizedValue,
      }

      if (edit) {
        updateSetting({ body })
        return
      }

      createSetting({ body })
    },
    [createSetting, edit, props.selectedSetting?.key, updateSetting, workspaceId]
  )

  if (isCreating || isUpdating) return <FormComponentLoading />

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} edit={edit} submitHandler={submitHandler} applicationSettings={applicationSettings} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} edit={edit} submitHandler={submitHandler} applicationSettings={applicationSettings} />
  )
}

function FormComponent({
  selectedSetting,
  submitHandler,
  edit,
  applicationSettings,
}: WorkspaceSettingFormProps & {
  edit: boolean
  submitHandler: (data: TWorkspaceSettingForm) => void
  applicationSettings: ApplicationSetting[]
}) {
  const settingTypeByKey = useMemo(
    () =>
      Object.fromEntries(
        applicationSettings
          .filter((setting) => !!setting.key && !!setting.type)
          .map((setting) => [setting.key!, setting.type as SettingType])
      ) as Record<string, SettingType>,
    [applicationSettings]
  )

  const keyOptions = useMemo(
    () =>
      applicationSettings
        .filter((setting) => !!setting.key)
        .map((setting) => ({
          value: setting.key!,
          label: setting.key!,
          description: setting.type ?? undefined,
        })),
    [applicationSettings]
  )

  const selectedKeyType = selectedSetting?.key ? settingTypeByKey[selectedSetting.key] : undefined

  const defaultValues = useMemo(
    () => ({
      key: selectedSetting?.key ?? '',
      value: selectedSetting?.value ?? '',
      valueType: selectedKeyType ?? 'string',
    }),
    [selectedKeyType, selectedSetting?.key, selectedSetting?.value]
  )

  const form = useForm<TWorkspaceSettingForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const selectedKey = form.watch('key')
  const selectedType = form.watch('valueType')

  const resolvedType = useMemo(() => {
    if (edit && selectedSetting?.key) {
      return settingTypeByKey[selectedSetting.key] ?? selectedType ?? 'string'
    }
    return settingTypeByKey[selectedKey] ?? selectedType ?? 'string'
  }, [edit, selectedKey, selectedSetting?.key, selectedType, settingTypeByKey])

  useEffect(() => {
    if (form.getValues('valueType') !== resolvedType) {
      form.setValue('valueType', resolvedType, { shouldValidate: true })
    }
  }, [form, resolvedType])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(submitHandler)}>
        <div className="flex h-full flex-col">
          <div className="space-y-6">
            <SingleSelectComboboxField
              control={form.control}
              name="key"
              label="Setting key"
              options={keyOptions}
              placeholder="Select a key..."
              searchPlaceholder="Search keys..."
              emptyMessage="No keys found."
              disabled={edit}
              allowClear={false}
            />
            <SingleSelectComboboxField
              control={form.control}
              name="valueType"
              label="Value type"
              options={settingTypeOptions}
              disabled
              allowClear={false}
            />
            <FormField
              control={form.control}
              name="value"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Setting value</FormLabel>
                  <FormControl>
                    {resolvedType === 'date' ? (
                      <Input type="date" {...field} />
                    ) : resolvedType === 'boolean' ? (
                      <SingleSelectCombobox
                        options={[
                          { value: 'true', label: 'true' },
                          { value: 'false', label: 'false' },
                        ]}
                        selectedValue={field.value || null}
                        onSelectionChange={(selectedValue) => field.onChange(selectedValue ?? '')}
                        placeholder="Select true/false..."
                        searchPlaceholder="Search options..."
                        emptyMessage="No options found."
                        allowClear={false}
                      />
                    ) : resolvedType === 'list' ? (
                      <Textarea
                        {...field}
                        placeholder="Comma separated values, e.g. one,two,three"
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                    ) : resolvedType === 'number' ? (
                      <Input
                        type="number"
                        step={1}
                        {...field}
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                    ) : (
                      <Input
                        {...field}
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="form-action-container">
            <Button type="submit">{edit ? 'Update' : 'Create'}</Button>
          </div>
        </div>
      </form>
    </Form>
  )
}
